import { Button, Card, CardContent, CircularProgress, Icon, Typography, Tooltip } from '@material-ui/core';
import type { StyledComponentProps } from '@material-ui/core/styles';
import User from 'classes/User';
import PermissionHide from 'components/PermissionHide';
import Transaction from 'components/Transaction';
import TransferForeign from 'components/TransferForeign';
import { FINANCIAL_BLOCK_TOOLTIP_MESSAGE } from 'helpers/constants';
import { userHasNoDocumentAlert } from 'helpers/functions';
import React, { Component, Fragment } from 'react';
import { FormattedNumber } from 'react-intl';
import { connect } from 'react-redux';
import { loadAvailable } from 'store/Orbita/Financial/actions';
import { FidcAlertModal } from 'v2/components/Shared/FidcAlertModal';
import ModalFiscal from 'v2/components/Shared/ModalFiscal';
import WithBetaModules from 'v2/components/Shared/WithBetaModules';

import type IProfile from 'v2/interfaces/profile';

import styles from './styles';

import type { DispatchProp } from 'react-redux';

interface IProps extends DispatchProp<any>, StyledComponentProps {
  balance: any;
  currentUser: User;
  currentUserIsFetching: any;
  loadAvailable: typeof loadAvailable;
}

interface IState {
  modal: boolean;
  modalFiscal: boolean;
  modalForeign: boolean;
  modalFidc: boolean;
  profile: IProfile;
}

class Wallet extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      modal: false,
      modalFiscal: false,
      modalForeign: false,
      modalFidc: false,
      profile: null,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = () => {
    this.props.loadAvailable();
  };

  handleModal = async () => {
    const { currentUser } = this.props;
    const { hasDocument, profile } = await userHasNoDocumentAlert();
    const showGalaxyEligibleAlert = currentUser?.eligible;

    if (showGalaxyEligibleAlert) {
      this.setState({ modalFidc: true });
      return;
    }

    if (!hasDocument) {
      this.setState({ modalFiscal: true, profile });
      return;
    }

    this.setState({
      modal: !this.state.modal,
    });
  };

  handleCloseFidcModal = () => {
    this.setState({
      modalFidc: false,
      modal: true,
    });
  };

  handleCloseModal = () => {
    this.setState({
      modal: false,
    });
  };

  handleCloseModalFiscal = () => {
    this.setState({
      modalFiscal: false,
    });
  };

  handleCompleteFiscalTerms = () => {
    this.setState(
      {
        modalFiscal: false,
      },
      this.handleModal
    );
  };

  render() {
    const { classes, balance, currentUser, currentUserIsFetching } = this.props;
    const { modalFidc } = this.state;
    const currency = 'BRL';
    const total_value = balance.total_value;
    const valor = balance.value;

    return (
      <>
        <Card elevation={0} className={classes.wallet} square>
          <CardContent className={classes.content}>
            <div className={classes.inline}>
              <div>
                <Typography color='inherit' gutterBottom variant='subtitle1' component='span' style={{ margin: 0, fontSize: 12 }}>
                  WALLET
                </Typography>
                <Typography color='inherit' component='h2' variant='h6'>
                  {currency}
                </Typography>
              </div>

              <div>
                <div>
                  <Typography align='right' component='h2' className={classes.title}>
                    Saldo total:
                  </Typography>
                  <Typography align='right' className={classes.subTitle}>
                    Soma do saldo disponível com o saldo futuro
                  </Typography>

                  <Typography align='right' component='h2' variant='h5'>
                    {balance && !balance.isFetching ? (
                      <Fragment>
                        {balance && (balance.total_value || balance.total_value === 0) && (
                          <Fragment>
                            <FormattedNumber format={currency} value={total_value} />{' '}
                          </Fragment>
                        )}
                      </Fragment>
                    ) : (
                      <CircularProgress size={20} />
                    )}
                  </Typography>
                </div>

                <div>
                  <Typography align='right' component='h2' className={classes.title}>
                    Saldo disponível para transferência:
                  </Typography>

                  <Typography align='right' className={classes.subTitle}>
                    Total de ganhos que já estão disponíveis para transferir
                  </Typography>

                  <Typography align='right' component='h2' variant='h5'>
                    {balance && !balance.isFetching ? (
                      <Fragment>
                        {balance && (balance.value || balance.value === 0) && (
                          <Fragment>
                            <FormattedNumber format={currency} value={valor} />{' '}
                          </Fragment>
                        )}
                      </Fragment>
                    ) : (
                      <CircularProgress size={20} />
                    )}
                  </Typography>
                </div>

                <div>
                  <Typography align='right' component='h2' className={classes.title}>
                    Saldo a antecipar:
                  </Typography>

                  <Typography align='right' className={classes.subTitle}>
                    Ganhos que podem ser antecipados
                  </Typography>

                  <Typography align='right' component='h2' variant='h5'>
                    {balance && !balance.isFetching ? (
                      <Fragment>
                        {balance && (balance.available_antecipation || balance.available_antecipation === 0) && (
                          <FormattedNumber format={currency} value={balance.available_antecipation} />
                        )}
                      </Fragment>
                    ) : (
                      <CircularProgress size={20} />
                    )}
                  </Typography>
                </div>
              </div>
            </div>

            <div className={classes.buttonContainer}>
              <PermissionHide action='myeduzz:dashboard:solicitar-transferencia'>
                <a
                  href='https://ajuda.eduzz.com/hc/pt-br/articles/4402057357211-Como-funciona-a-antecipa%C3%A7%C3%A3o-%C3%93rbita-'
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Entenda a antecipação
                </a>

                <WithBetaModules>
                  {(betaModules: string[]) => {
                    if (!currentUserIsFetching && currentUser && !currentUser.has_transfer) {
                      const userHasBlockTransferBetaModule = betaModules?.includes('block_transfer');
                      return (
                        <Tooltip
                          disableHoverListener={!userHasBlockTransferBetaModule}
                          disableFocusListener={!userHasBlockTransferBetaModule}
                          disableTouchListener={!userHasBlockTransferBetaModule}
                          title={FINANCIAL_BLOCK_TOOLTIP_MESSAGE}
                        >
                          <div>
                            <Button
                              id='myTransferButton'
                              className={classes.transferButton}
                              disabled={(balance && balance.isFetching) || userHasBlockTransferBetaModule}
                              onClick={this.handleModal}
                            >
                              <Icon style={{ marginRight: '0.5rem', fontSize: 14 }}>attach_money</Icon>Transferir agora
                            </Button>
                          </div>
                        </Tooltip>
                      );
                    }

                    return null;
                  }}
                </WithBetaModules>
                {currentUser && currentUser.has_transfer && <div>Você tem transferências em processamento</div>}
              </PermissionHide>
            </div>
          </CardContent>
        </Card>
        {this.state.modal && (
          <Transaction
            selfClose={this.handleCloseModal}
            successCallback={this.loadData}
            open={this.state.modal}
            onCloseModalTransfer={this.handleCloseModal}
          />
        )}
        {this.state.modalForeign && (
          <TransferForeign
            value={valor}
            selfClose={this.handleModal}
            successCallback={this.loadData}
            open={this.state.modalForeign}
            onCloseModalTransfer={this.handleModal}
          />
        )}
        <ModalFiscal
          opened={this.state.modalFiscal}
          onClose={this.handleCloseModalFiscal}
          onComplete={this.handleCompleteFiscalTerms}
          profile={this.state.profile}
        />
        <FidcAlertModal isOpen={modalFidc} onClose={this.handleCloseFidcModal} />
      </>
    );
  }
}

function mapStateToProps(state: any) {
  return {
    balance: state.orbita.Financial.loadAvailable,
    currentUser: state.orbita.User.user ? new User(state.orbita.User.user) : null,
    currentUserIsFetching: state.orbita.User.user.isFetching,
  };
}

export default connect(mapStateToProps, { loadAvailable })(styles(Wallet));
